import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EMPTY, Observable, map, tap } from 'rxjs';
import { Booking, BookingPayment, BookingPlayer, BookingStatus } from '../model/booking.model';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';

export type BookingScope = 'upcoming' | 'past' | 'all';

interface BookingDto {
  id?: string;
  /** Unified endpoint discriminator: 'booking' | 'alert' | 'waitlist'. */
  type?: 'booking' | 'alert' | 'waitlist' | string;
  bookingId?: string;
  waitlistId?: string;
  waitlistReferenceId?: string;
  alertId?: string;
  alertReferenceId?: string;
  referenceId?: string;
  teefoxRequestId?: string;
  externalId?: string;
  status?: string;
  courseName?: string;
  location?: string;
  /** Flat list-endpoint shape (v2): course location lives at the top level. */
  courseLocation?: string;
  /** Flat list-endpoint shape (v2): pre-formatted, already-localised display fields. */
  displayTime?: string;
  dayOfWeek?: string;
  dayOfMonth?: number;
  monthShort?: string;
  year?: number;
  isoDate?: string;
  course?: {
    name?: string;
    location?: string;
    countryCode?: string;
    country?: string;
    locationId?: string;
    locationDisplay?: string;
  };
  teeTimeAt?: string;
  apptTime?: string;
  date?: string;
  time?: string;
  teeTime?: {
    time?: string;
    displayTime?: string;
    date?: string;
    apptTime?: string;
    isoDate?: string;
    dayOfWeek?: string;
    dayOfMonth?: number;
    monthShort?: string;
    year?: number;
    players?: number;
    holes?: number;
  };
  statusLabel?: string;
  statusColor?: string;
  players?: number | BookingPlayer[];
  holes?: number;
  pricePerPatron?: number;
  totalPrice?: number;
  currency?: string;
  contact?: { name?: string; email?: string; phone?: string; avatarUrl?: string };
  user?: { name?: string; email?: string; phone?: string; avatarUrl?: string | null };
  customer?: { name?: string; email?: string; phone?: string; avatarUrl?: string | null };
  costLines?: { label: string; amount: number }[];
  costSummary?: {
    perPlayer?: { label: string; amount: number }[];
    bookingFee?: number;
    totalDue?: number;
    totalPaid?: number;
  };
  bookingFee?: number;
  totalPaid?: number;
  confirmationCode?: string;
  confirmationNumber?: string | null;
  cancellationPolicy?: string;
  courseLogoUrl?: string;
  place?: string;
  coordinates?: { lat?: number; lng?: number } | null;
  /** Prepay payment block — present only on the per-id detail endpoint. */
  payment?: {
    url?: string;
    expiresAt?: string;
    reference?: string;
    amount?: number;
    currency?: string;
    status?: string;
  };
}

interface BookingsResponse {
  bookings?: BookingDto[];
  results?: BookingDto[];
  data?: BookingDto[];
  scope?: BookingScope;
  page?: number;
  limit?: number;
  totalPages?: number;
  totalResults?: number;
  upcomingCount?: number;
  pastCount?: number;
  /** Per-type totals returned by the unified endpoint, e.g. { booking, alert, waitlist } */
  counts?: { booking?: number; alert?: number; waitlist?: number };
}

export interface BookingsPageOptions {
  page?: number;
  limit?: number;
}

@Injectable({ providedIn: 'root' })
export class BookingService {
  private readonly _http = inject(HttpClient);
  private readonly _authService = inject(AuthService);

  private readonly _bookings = signal<Booking[]>([]);
  private readonly _pastBookings = signal<Booking[]>([]);
  private readonly _loading = signal(false);
  private readonly _upcomingCount = signal(0);
  private readonly _pastCount = signal(0);

  readonly bookings = this._bookings.asReadonly();
  readonly pastBookings = this._pastBookings.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly upcomingCount = this._upcomingCount.asReadonly();
  readonly pastCount = this._pastCount.asReadonly();

  getById(id: string): Booking | undefined {
    return this._bookings().find(b => b.id === id);
  }

  loadBookings(scope: BookingScope = 'upcoming', opts: BookingsPageOptions = {}): Observable<Booking[]> {
    if (!this._authService.isAuthenticated()) return EMPTY;
    this._loading.set(true);
    const { page = 1, limit = 20 } = opts;
    const params = new HttpParams()
      .set('scope', scope)
      .set('page', page)
      .set('limit', limit);
    return this._http
      .get<BookingsResponse | BookingDto[]>(`${environment.apiUrl}bookings`, { params })
      .pipe(
        tap((res) => this.captureCounts(res)),
        map((res) =>
          Array.isArray(res)
            ? res
            : res?.bookings ?? res?.results ?? res?.data ?? [],
        ),
        map((dtos: BookingDto[]) => dtos.map((dto) => this.mapDtoToBooking(dto))),
        tap({
          next: (bookings) => {
            this._bookings.set(bookings);
            this._loading.set(false);
          },
          error: () => this._loading.set(false),
        }),
      );
  }

  loadPastBookings(opts: BookingsPageOptions = {}): Observable<Booking[]> {
    if (!this._authService.isAuthenticated()) return EMPTY;
    this._loading.set(true);
    const { page = 1, limit = 20 } = opts;
    const params = new HttpParams()
      .set('scope', 'past')
      .set('page', page)
      .set('limit', limit);
    return this._http
      .get<BookingsResponse | BookingDto[]>(`${environment.apiUrl}bookings`, { params })
      .pipe(
        tap((res) => this.captureCounts(res)),
        map((res) =>
          Array.isArray(res)
            ? res
            : res?.bookings ?? res?.results ?? res?.data ?? [],
        ),
        map((dtos: BookingDto[]) => dtos.map((dto) => this.mapDtoToBooking(dto))),
        tap({
          next: (bookings) => {
            this._pastBookings.set(bookings);
            this._loading.set(false);
          },
          error: () => this._loading.set(false),
        }),
      );
  }

  private captureCounts(res: BookingsResponse | BookingDto[]): void {
    if (Array.isArray(res)) return;

    // Legacy shape: explicit upcoming/past counts on the response.
    if (typeof res.upcomingCount === 'number') this._upcomingCount.set(res.upcomingCount);
    if (typeof res.pastCount === 'number') this._pastCount.set(res.pastCount);

    // Unified endpoint shape: results contain all types (booking/alert/waitlist)
    // paginated together, so totalResults is the scoped total that drives pagination.
    if (typeof res.totalResults === 'number') {
      if (res.scope === 'past') {
        this._pastCount.set(res.totalResults);
      } else if (res.scope === 'upcoming') {
        this._upcomingCount.set(res.totalResults);
      }
    }
  }

  loadBookingById(id: string): Observable<Booking> {
    return this._http
      .get<BookingDto | { booking?: BookingDto; data?: BookingDto }>(`${environment.apiUrl}bookings/${id}`)
      .pipe(
        map((res) => {
          if (res && typeof res === 'object' && 'booking' in res && res.booking) return res.booking;
          if (res && typeof res === 'object' && 'data' in res && res.data) return res.data;
          return res as BookingDto;
        }),
        map((dto) => this.mapDtoToBooking(dto)),
        tap((booking) => {
          const current = this._bookings();
          const idx = current.findIndex((b) => b.id === booking.id);
          if (idx >= 0) {
            const next = [...current];
            next[idx] = booking;
            this._bookings.set(next);
          } else {
            this._bookings.set([...current, booking]);
          }
        }),
      );
  }

  private mapDtoToBooking(dto: BookingDto): Booking {
    // Flat list shape (v2) lifts these to the top level; nested detail shape keeps them under teeTime.
    const dayOfWeek = dto.teeTime?.dayOfWeek ?? dto.dayOfWeek;
    const dayOfMonth = dto.teeTime?.dayOfMonth ?? dto.dayOfMonth;
    const monthShort = dto.teeTime?.monthShort ?? dto.monthShort;
    const year = dto.teeTime?.year ?? dto.year;
    const displayTime = dto.teeTime?.displayTime ?? dto.displayTime;

    const isoDate =
      dto.teeTime?.isoDate ?? dto.isoDate ?? dto.teeTime?.apptTime ?? dto.apptTime ?? dto.teeTimeAt;
    const parsedDate = isoDate ? new Date(isoDate) : null;
    // The flat list endpoint omits an ISO timestamp, so reconstruct a local Date from the
    // pre-formatted parts. This keeps list sorting (which compares date.getTime()) correct
    // without relying on the now-defaulted `new Date()`.
    const date =
      parsedDate && !Number.isNaN(parsedDate.getTime())
        ? parsedDate
        : this.buildDateFromParts(year, monthShort, dayOfMonth, displayTime) ?? new Date();

    const playerCount = Array.isArray(dto.players)
      ? dto.players.length
      : dto.teeTime?.players ?? (typeof dto.players === 'number' ? dto.players : 0);
    const players: BookingPlayer[] = Array.isArray(dto.players)
      ? dto.players
      : Array.from({ length: playerCount }, () => ({} as BookingPlayer));

    const customer = dto.customer ?? dto.user;
    const contact = dto.contact ?? (customer
      ? {
          name: customer.name,
          email: customer.email,
          phone: customer.phone,
          avatarUrl: (customer as { avatarUrl?: string | null }).avatarUrl ?? undefined,
        }
      : undefined);

    const pricePerPatron = dto.pricePerPatron;
    const totalPrice = dto.totalPrice;
    const costLines = dto.costLines
      ?? dto.costSummary?.perPlayer?.map((l) => ({ label: l.label, amount: l.amount }))
      ?? (pricePerPatron && playerCount
        ? Array.from({ length: playerCount }, (_, i) => ({
            label: `${i + 1}x Player`,
            amount: pricePerPatron / 100,
          }))
        : undefined);
    const bookingFee = dto.bookingFee ?? dto.costSummary?.bookingFee;
    const totalPaid =
      dto.totalPaid
      ?? dto.costSummary?.totalPaid
      ?? dto.costSummary?.totalDue
      ?? (totalPrice !== undefined ? totalPrice / 100 : undefined);

    const status = this.normaliseStatus(dto.status ?? dto.statusLabel);

    const rawCoords = dto.coordinates;
    const coordinates =
      rawCoords && typeof rawCoords.lat === 'number' && typeof rawCoords.lng === 'number'
        ? { lat: rawCoords.lat, lng: rawCoords.lng }
        : undefined;

    const rawApptTime = dto.teeTime?.apptTime ?? dto.apptTime ?? dto.teeTimeAt;
    const bookingId = dto.bookingId ?? dto.id;
    const referenceId = dto.referenceId ?? dto.teefoxRequestId ?? dto.externalId;

    // Unified endpoint tags each row with a type so the detail page knows which
    // collection to fetch from (alerts → /alerts, waitlists → /waitlists).
    const isAlert = dto.type === 'alert';
    const isWaitlist = dto.type === 'waitlist';

    const payment = this.mapPayment(dto.payment);

    return {
      id: dto.id ?? dto.bookingId ?? dto.referenceId ?? '',
      date,
      time: displayTime ?? dto.teeTime?.time ?? dto.time ?? '',
      dayOfWeek,
      dayOfMonth,
      monthShort,
      year,
      status,
      statusLabel: dto.statusLabel ?? this.defaultStatusLabel(status),
      statusColor: dto.statusColor ?? this.defaultStatusColor(status),
      courseName: dto.course?.name ?? dto.courseName ?? '',
      location:
        dto.course?.locationDisplay || dto.course?.location || dto.location || dto.courseLocation || '',
      locationId: dto.course?.locationId,
      place: dto.place,
      coordinates,
      players,
      holes: dto.teeTime?.holes ?? dto.holes,
      apptTime: rawApptTime,
      bookingId,
      referenceId,
      costLines,
      bookingFee,
      totalPaid,
      confirmationCode: dto.confirmationCode ?? dto.confirmationNumber ?? undefined,
      cancellationPolicy: dto.cancellationPolicy,
      contact,
      courseLogoUrl: dto.courseLogoUrl,
      isAlert: isAlert || undefined,
      alertId: isAlert ? (dto.alertId ?? dto.id) : undefined,
      alertReferenceId: isAlert ? (dto.alertReferenceId ?? referenceId) : undefined,
      isWaitlist: isWaitlist || undefined,
      waitlistId: isWaitlist ? (dto.waitlistId ?? dto.id) : undefined,
      waitlistReferenceId: isWaitlist ? (dto.waitlistReferenceId ?? referenceId) : undefined,
      payment,
    };
  }

  /** Normalise the raw per-id payment block into a typed BookingPayment (or undefined when absent). */
  private mapPayment(raw: BookingDto['payment']): BookingPayment | undefined {
    if (!raw || !raw.status) return undefined;
    const allowed = ['pending', 'paid', 'refunded', 'not_applicable', 'expired', 'cancelled'] as const;
    const status = (allowed as readonly string[]).includes(raw.status)
      ? (raw.status as BookingPayment['status'])
      : 'not_applicable';
    return {
      url: raw.url ?? '',
      expiresAt: raw.expiresAt,
      reference: raw.reference,
      amount: raw.amount,
      currency: raw.currency,
      status,
    };
  }

  /** Three-letter month abbreviations → 0-based month index, for the flat list shape. */
  private static readonly MONTH_INDEX: Record<string, number> = {
    JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
    JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
  };

  /** Rebuild a local Date from the flat list endpoint's pre-formatted parts.
   *  Returns undefined when the essential parts (month + day) are missing. */
  private buildDateFromParts(
    year: number | undefined,
    monthShort: string | undefined,
    dayOfMonth: number | undefined,
    displayTime: string | undefined,
  ): Date | undefined {
    if (monthShort == null || dayOfMonth == null) return undefined;
    const monthIndex = BookingService.MONTH_INDEX[monthShort.trim().toUpperCase()];
    if (monthIndex === undefined) return undefined;
    const y = year ?? new Date().getFullYear();
    const { hours, minutes } = this.parseDisplayTime(displayTime);
    return new Date(y, monthIndex, dayOfMonth, hours, minutes, 0, 0);
  }

  /** Parse a display time like "1:00PM" / "11:30 am" into 24h hours/minutes.
   *  Defaults to midnight when absent or unparseable. */
  private parseDisplayTime(displayTime: string | undefined): { hours: number; minutes: number } {
    if (!displayTime) return { hours: 0, minutes: 0 };
    const match = /(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(displayTime);
    if (!match) return { hours: 0, minutes: 0 };
    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const meridiem = match[3]?.toUpperCase();
    if (meridiem === 'PM' && hours < 12) hours += 12;
    if (meridiem === 'AM' && hours === 12) hours = 0;
    return { hours, minutes };
  }

  private defaultStatusLabel(status: BookingStatus): string {
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  private defaultStatusColor(status: BookingStatus): string {
    switch (status) {
      case 'booked': return 'green';
      case 'waitlist': return 'yellow';
      case 'processing': return 'yellow';
      case 'cancelled': return 'grey';
      case 'completed': return 'grey';
      default: return 'grey';
    }
  }

  private normaliseStatus(raw: string | undefined): BookingStatus {
    switch ((raw ?? '').toLowerCase()) {
      case 'booked':
      case 'confirmed':
        return 'booked';
      case 'waitlist':
      case 'waitlisted':
        return 'waitlist';
      case 'processing':
      case 'pending':
        return 'processing';
      case 'cancelled':
      case 'canceled':
      case 'cancellation_failed':
      case 'cancellation_pending':
        return 'cancelled';
      case 'completed':
      case 'played':
        return 'completed';
      default:
        return 'booked';
    }
  }
}
